import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@mlm/ui';
import { listPosMaterials, listPosProducts, listPosShelfInfo } from '../lib/api';
import { useActiveBranch } from '../lib/use-branch';
import { BranchPicker } from '../components/branch-picker';
import { InventoryLog } from '../components/inventory-log';

/**
 * Inventory tab (feature 13) — current stock of the branch. Reuses the same
 * branch-aware product catalog the checkout grid reads (`/pos/products`); this
 * is the read-only stock view with a name/SKU filter and a low-stock highlight.
 * Per-row "View log" opens the product's slice of the append-only
 * `inventory_movements` ledger in a side sheet (mirroring the admin Branch
 * page); the All movements card below is the same log branch-wide.
 */
export function InventoryPage() {
  const { branchId, setSelectedBranchId, isScoped, branches } = useActiveBranch();
  const [filter, setFilter] = useState('');
  const [logProduct, setLogProduct] = useState<{ id: string; name: string } | null>(null);

  const query = useQuery({
    queryKey: ['pos-inventory', branchId],
    enabled: !!branchId,
    queryFn: () => listPosProducts(branchId!),
  });

  // Materials (cups, lids) are used up by recipes and never rung up, so the
  // catalog above does not carry them. `retry: false` because a till without
  // `pos.inventory.view` gets a 403 here, and the section just stays hidden.
  const materialsQuery = useQuery({
    queryKey: ['pos-materials', branchId],
    enabled: !!branchId,
    queryFn: () => listPosMaterials(branchId!),
    retry: false,
  });

  // Item locator + next expiry. Sparse and switch-gated on the server: with
  // both switches off this is an empty list and neither column renders.
  const shelfQuery = useQuery({
    queryKey: ['pos-shelf-info', branchId],
    enabled: !!branchId,
    queryFn: () => listPosShelfInfo(branchId!),
    retry: false,
  });
  const shelf = useMemo(
    () => new Map((shelfQuery.data ?? []).map((r) => [r.productId, r])),
    [shelfQuery.data],
  );
  const showLocation = (shelfQuery.data ?? []).some((r) => r.locationAisle || r.locationRack);
  const showExpiry = (shelfQuery.data ?? []).some((r) => r.nearestExpiresOn);
  const locationOf = (productId: string) => {
    const r = shelf.get(productId);
    return [r?.locationAisle, r?.locationRack].filter(Boolean).join(' / ');
  };
  // The till's own calendar date is close enough to flag "already expired":
  // the authoritative aging view is the admin's Expiring Stock page.
  const todayIso = new Date().toLocaleDateString('en-CA');
  const expiryCell = (productId: string) => {
    const date = shelf.get(productId)?.nearestExpiresOn;
    if (!date) return <span className="text-muted-foreground">-</span>;
    return <span className={date < todayIso ? 'font-medium text-destructive' : ''}>{date}</span>;
  };

  const matches = (name: string, sku: string) => {
    const f = filter.trim().toLowerCase();
    return !f || name.toLowerCase().includes(f) || sku.toLowerCase().includes(f);
  };
  const rows = (query.data ?? []).filter((p) => matches(p.name, p.sku));
  const materials = (materialsQuery.data ?? []).filter((m) => matches(m.name, m.sku));

  const stockClass = (qty: number) =>
    qty <= 0 ? 'text-destructive' : qty < 5 ? 'text-warning' : '';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Branch inventory</CardTitle>
          <BranchPicker
            branches={branches}
            branchId={branchId}
            isScoped={isScoped}
            onChange={setSelectedBranchId}
          />
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name or SKU…"
            className="max-w-xs"
          />
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !rows.length ? (
            <p className="text-sm text-muted-foreground">No products match.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="px-2 py-1.5">SKU</th>
                  <th className="px-2 py-1.5">Product</th>
                  {showLocation && <th className="px-2 py-1.5">Location</th>}
                  {showExpiry && <th className="px-2 py-1.5">Next expiry</th>}
                  <th className="px-2 py-1.5 text-right">Price</th>
                  <th className="px-2 py-1.5 text-right">On hand</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <Fragment key={p.id}>
                    <tr className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-2 py-1.5 font-mono text-xs">{p.sku}</td>
                      <td className="px-2 py-1.5">{p.name}</td>
                      {showLocation && (
                        <td className="px-2 py-1.5 text-xs text-muted-foreground">
                          {locationOf(p.id) || '-'}
                        </td>
                      )}
                      {showExpiry && (
                        <td className="px-2 py-1.5 text-xs tabular-nums">{expiryCell(p.id)}</td>
                      )}
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        ₱{Number(p.effectivePrice).toFixed(2)}
                      </td>
                      <td
                        className={`px-2 py-1.5 text-right tabular-nums font-medium ${stockClass(p.qty)}`}
                      >
                        {p.qty}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          title="View movement log"
                          onClick={() => setLogProduct({ id: p.id, name: p.name })}
                        >
                          View log
                        </Button>
                      </td>
                    </tr>
                    {/* A variation product's stock lives per-variant — surface
                        the split the payload already carries. */}
                    {p.variants.map((v) => (
                      <tr key={v.id} className="border-b last:border-0 text-xs">
                        <td className="px-2 py-1 pl-6 font-mono text-muted-foreground">{v.sku}</td>
                        <td className="px-2 py-1 text-muted-foreground">↳ {v.name}</td>
                        {showLocation && <td></td>}
                        {showExpiry && <td></td>}
                        <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                          ₱{Number(v.price).toFixed(2)}
                        </td>
                        <td
                          className={`px-2 py-1 text-right tabular-nums ${stockClass(v.stockQty)}`}
                        >
                          {v.stockQty}
                        </td>
                        <td></td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Only when the tenant actually keeps materials: most do not, and an
          empty card would be noise on every other till. */}
      {(materialsQuery.data?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Materials</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Supplies that are used up when something sells (cups, lids). They are never rung up on
              their own.
            </p>
            {!materials.length ? (
              <p className="text-sm text-muted-foreground">No materials match.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="px-2 py-1.5">SKU</th>
                    <th className="px-2 py-1.5">Material</th>
                    {showLocation && <th className="px-2 py-1.5">Location</th>}
                    {showExpiry && <th className="px-2 py-1.5">Next expiry</th>}
                    <th className="px-2 py-1.5 text-right">On hand</th>
                    <th className="px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((m) => (
                    <tr key={m.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-2 py-1.5 font-mono text-xs">{m.sku}</td>
                      <td className="px-2 py-1.5">{m.name}</td>
                      {showLocation && (
                        <td className="px-2 py-1.5 text-xs text-muted-foreground">
                          {locationOf(m.id) || '-'}
                        </td>
                      )}
                      {showExpiry && (
                        <td className="px-2 py-1.5 text-xs tabular-nums">{expiryCell(m.id)}</td>
                      )}
                      <td
                        className={`px-2 py-1.5 text-right tabular-nums font-medium ${
                          m.qty <= 0 ? 'text-destructive' : ''
                        }`}
                      >
                        {m.qty}
                        {m.unitLabel ? (
                          <span className="ml-1 text-xs font-normal text-muted-foreground">
                            {m.unitLabel}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          title="View movement log"
                          onClick={() => setLogProduct({ id: m.id, name: m.name })}
                        >
                          View log
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {branchId && (
        <Card>
          <CardHeader>
            <CardTitle>All movements</CardTitle>
          </CardHeader>
          <CardContent>
            <InventoryLog branchId={branchId} showFilters />
          </CardContent>
        </Card>
      )}

      <Sheet open={!!logProduct} onOpenChange={(o) => !o && setLogProduct(null)}>
        <SheetContent side="right" className="overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Inventory log{logProduct ? ` · ${logProduct.name}` : ''}</SheetTitle>
            <SheetDescription>
              Every stock movement for this product at this branch, newest first.
            </SheetDescription>
          </SheetHeader>
          {branchId && logProduct && (
            <div className="mt-4">
              <InventoryLog branchId={branchId} productId={logProduct.id} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
